import { useState, useRef, useEffect } from 'react';
import { ExclamationCircleIcon, ExclamationTriangleIcon } from '@c2fo/liquidity/icons';
import type { HelpSeverity } from '../../api/Credentials/types';

interface FieldHelpTooltipProps {
  helpText: string;
  severity?: HelpSeverity;
  className?: string;
}

export const FieldHelpTooltip: React.FC<FieldHelpTooltipProps> = ({ helpText, severity = 'info', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isWarning = severity === 'warning';

  const iconClasses = isWarning ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-gray-600';

  const tooltipClasses = isWarning
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-white border-gray-200 text-gray-700';

  const Icon = isWarning ? ExclamationTriangleIcon : ExclamationCircleIcon;

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className={`p-0.5 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-1 ${
          isWarning ? 'focus:ring-amber-500' : 'focus:ring-primary-500'
        } ${iconClasses}`}
        aria-label="Help"
        aria-expanded={isOpen}
        aria-describedby="help-tooltip"
      >
        <Icon className="h-4 w-4" />
      </button>

      {isOpen && (
        <div
          ref={tooltipRef}
          id="help-tooltip"
          role="tooltip"
          className={`absolute z-50 w-80 p-3 text-sm rounded-lg border shadow-lg ${tooltipClasses}`}
          style={{
            left: '0',
            top: 'calc(100% + 8px)',
          }}
        >
          <div className="whitespace-pre-wrap">{helpText}</div>
          <div
            className={`absolute -top-2 left-2 w-3 h-3 rotate-45 border-l border-t ${
              isWarning ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
            }`}
          />
        </div>
      )}
    </div>
  );
};
